// src/pages/hr/recruitment/JobOpenings.jsx
import React, { useState } from "react";
import { useRecruitmentContext } from "../../../context/RecruitmentContext";
import {
  createJobOpening,
  updateJobOpening,
  deleteJobOpening,
  deleteInterview,
  logRecruitmentActivity,
} from "../../../hooks/useRecruitment";
import { useAuth } from "../../../context/AuthContext";
import { toast } from "react-hot-toast";
import { Search, X, Plus, Edit2, Copy, Eye, Lock, Trash2, Building2, Briefcase, MapPin, AlertCircle } from "lucide-react";
import { EMS_DEPARTMENTS } from "../../../utils/hrConstants";

export default function JobOpenings() {
  const { jobOpenings, jobsLoading, candidates, interviews } = useRecruitmentContext();
  const { userProfile } = useAuth();

  const role = (userProfile?.role || "").toLowerCase();
  const isHR = role === "hr" || role === "admin" || role.includes("hr") || role.includes("admin") || !userProfile?.role;

  const [search, setSearch] = useState("");
  const [filterDept, setFilterDept] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [showModal, setShowModal] = useState(false);
  const [editingJob, setEditingJob] = useState(null);

  const [deleteConfirmJob, setDeleteConfirmJob] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [formData, setFormData] = useState({
    title: "",
    department: "Engineering",
    vacancies: 1,
    experienceRequired: "2-4 years",
    employmentType: "Full-Time",
    location: "On-site",
    priority: "Medium",
    hiringManager: "",
    deadline: "",
    description: "",
    skillsRequired: "React, JavaScript",
    status: "Open",
  });

  const depts = Array.from(new Set(jobOpenings.map((j) => j.department || "General")));

  const filteredJobs = jobOpenings.filter((j) => {
    const matchSearch =
      (j.title || "").toLowerCase().includes(search.toLowerCase()) ||
      (j.id || "").toLowerCase().includes(search.toLowerCase()) ||
      (j.department || "").toLowerCase().includes(search.toLowerCase());
    const matchDept = filterDept === "all" || j.department === filterDept;
    const matchStatus = filterStatus === "all" || j.status === filterStatus;
    return matchSearch && matchDept && matchStatus;
  });

  function handleOpenCreate() {
    setEditingJob(null);
    setFormData({
      title: "",
      department: "Engineering",
      vacancies: 1,
      experienceRequired: "2-4 years",
      employmentType: "Full-Time",
      location: "On-site",
      priority: "Medium",
      hiringManager: userProfile?.displayName || "HR Admin",
      deadline: "",
      description: "",
      skillsRequired: "React, JavaScript",
      status: "Open",
    });
    setShowModal(true);
  }

  function handleOpenEdit(job) {
    setEditingJob(job);
    setFormData({
      title: job.title || "",
      department: job.department || "Engineering",
      vacancies: job.vacancies || 1,
      experienceRequired: job.experienceRequired || "2-4 years",
      employmentType: job.employmentType || "Full-Time",
      location: job.location || "On-site",
      priority: job.priority || "Medium",
      hiringManager: job.hiringManager || "",
      deadline: job.deadline || "",
      description: job.description || "",
      skillsRequired: Array.isArray(job.skillsRequired) ? job.skillsRequired.join(", ") : job.skillsRequired || "",
      status: job.status || "Open",
    });
    setShowModal(true);
  }

  async function handleDuplicate(job) {
    const newJobData = {
      ...job,
      title: `${job.title} (Copy)`,
      status: "Open",
      createdByName: userProfile?.displayName || "HR",
    };
    delete newJobData.id;
    await createJobOpening(newJobData);
    toast.success(`Job Opening "${job.title}" duplicated successfully!`);
  }

  async function handleCloseJob(id) {
    if (window.confirm("Are you sure you want to close this job opening?")) {
      await updateJobOpening(id, { status: "Closed" });
      toast.success("Job Opening marked as Closed.");
    }
  }

  function handleInitiateDelete(job) {
    if (!isHR) {
      toast.error("Only HR users have permission to delete job openings.");
      return;
    }

    const activeCandidates = (candidates || []).filter((c) => c.jobId === job.id);
    const applicantCount = job.applicantCount ?? 0;

    if (applicantCount > 0 || activeCandidates.length > 0) {
      toast.error(
        "This job has active candidates. Please close the job or remove all applicants before deleting.",
        { duration: 5000 }
      );
      return;
    }

    setDeleteConfirmJob(job);
  }

  async function handleConfirmDelete() {
    if (!deleteConfirmJob) return;
    setDeleting(true);

    try {
      const relatedInterviews = (interviews || []).filter((i) => i.jobId === deleteConfirmJob.id);
      for (const item of relatedInterviews) {
        await deleteInterview(item.id);
      }

      await deleteJobOpening(deleteConfirmJob.id);

      await logRecruitmentActivity({
        actorId: userProfile?.uid || "",
        actorName: userProfile?.displayName || userProfile?.name || "HR Admin",
        actorRole: userProfile?.role || "hr",
        action: "JOB_DELETED",
        entityType: "jobOpening",
        entityId: deleteConfirmJob.id,
        entityName: deleteConfirmJob.title || deleteConfirmJob.id,
        jobId: deleteConfirmJob.id,
      });

      toast.success("Job Opening deleted successfully.");
      setDeleteConfirmJob(null);
    } catch (err) {
      toast.error("Failed to delete job opening: " + err.message);
    } finally {
      setDeleting(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const skillsArr = typeof formData.skillsRequired === "string"
      ? formData.skillsRequired.split(",").map((s) => s.trim()).filter(Boolean)
      : formData.skillsRequired;

    const payload = {
      ...formData,
      vacancies: Number(formData.vacancies),
      skillsRequired: skillsArr,
      createdByName: userProfile?.displayName || "HR Admin",
    };

    if (editingJob) {
      await updateJobOpening(editingJob.id, payload);
      toast.success("Job Opening updated successfully!");
    } else {
      await createJobOpening(payload);
      toast.success("Job Opening created successfully!");
    }
    setShowModal(false);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
      <style>{`
        .ats-toolbar { display: flex; gap: 0.85rem; flex-wrap: wrap; justify-content: space-between; align-items: center; margin-bottom: 0.25rem; }
        .ats-filter-group { display: flex; gap: 0.6rem; flex-wrap: wrap; align-items: center; }
        
        .ats-search-box {
          position: relative;
          display: flex;
          align-items: center;
          flex: 1;
          min-width: 280px;
          max-width: 460px;
          background: var(--bg-card, #ffffff);
          border: 1.5px solid var(--border, #e2e8f0);
          border-radius: 10px;
          padding: 0 0.85rem;
          height: 42px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
          transition: all 0.2s ease-in-out;
        }
        .ats-search-box:focus-within {
          border-color: #1b4b31;
          box-shadow: 0 0 0 3.5px rgba(27, 75, 49, 0.12), 0 2px 8px rgba(27, 75, 49, 0.06);
          background: #ffffff;
        }
        .ats-search-icon {
          color: #64748b;
          flex-shrink: 0;
          margin-right: 0.65rem;
          transition: color 0.2s ease;
        }
        .ats-search-box:focus-within .ats-search-icon {
          color: #1b4b31;
        }
        .ats-search-input {
          width: 100%;
          border: none;
          outline: none;
          background: transparent;
          font-size: 0.86rem;
          font-weight: 500;
          color: var(--text-primary, #0f172a);
          padding: 0;
        }
        .ats-search-input::placeholder {
          color: #94a3b8;
          font-weight: 400;
        }
        .ats-search-clear {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border: none;
          background: #e2e8f0;
          color: #475569;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          cursor: pointer;
          padding: 0;
          margin-left: 0.5rem;
          transition: all 0.15s ease;
          flex-shrink: 0;
        }
        .ats-search-clear:hover {
          background: #cbd5e1;
          color: #0f172a;
        }

        .ats-select {
          height: 42px;
          padding: 0 0.85rem;
          border: 1.5px solid var(--border, #e2e8f0);
          border-radius: 10px;
          font-size: 0.85rem;
          font-weight: 500;
          background-color: var(--bg-card, #fff);
          color: var(--text-primary, #1e293b);
          outline: none;
          cursor: pointer;
          transition: all 0.2s ease-in-out;
        }
        .ats-select:focus {
          border-color: #1b4b31;
          box-shadow: 0 0 0 3px rgba(27, 75, 49, 0.12);
        }

        .btn-primary {
          height: 42px;
          padding: 0 1.25rem;
          background: linear-gradient(135deg, #1b4b31 0%, #065f46 100%);
          color: #fff;
          border: none;
          border-radius: 10px;
          font-weight: 700;
          font-size: 0.85rem;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          box-shadow: 0 4px 12px rgba(27, 75, 49, 0.2);
          transition: all 0.2s ease;
        }
        .btn-primary:hover {
          background: linear-gradient(135deg, #143724 0%, #044e39 100%);
          transform: translateY(-1px);
          box-shadow: 0 6px 16px rgba(27, 75, 49, 0.3);
        }

        .job-card-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(500px, 1fr));
          gap: 1.25rem;
        }
        @media (max-width: 640px) {
          .job-card-grid { grid-template-columns: 1fr; }
        }

        .ejc-card {
          background: var(--bg-card, #ffffff);
          border: 1px solid var(--border, #e2e8f0);
          border-radius: 16px;
          padding: 1.4rem 1.6rem;
          box-shadow: 0 4px 16px rgba(15, 23, 42, 0.04);
          display: flex;
          flex-direction: column;
          gap: 1.1rem;
          transition: all 0.2s ease;
        }
        .ejc-card:hover {
          box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
          border-color: #cbd5e1;
        }

        .ejc-top { display: flex; justify-content: space-between; align-items: flex-start; gap: 0.85rem; }
        .ejc-left { display: flex; flex-direction: column; gap: 0.25rem; flex: 1; }
        .ejc-id { font-size: 0.72rem; font-weight: 700; color: #1b4b31; letter-spacing: 0.05em; background: #eaf4ee; border: 1px solid #a7f3d0; padding: 0.15rem 0.5rem; border-radius: 6px; width: fit-content; }
        .ejc-title { font-size: 1.15rem; font-weight: 800; color: #0f172a; line-height: 1.35; margin-top: 2px; }
        .ejc-meta { display: flex; flex-wrap: wrap; gap: 0.4rem; margin-top: 4px; }
        .ejc-meta-chip { font-size: 0.74rem; font-weight: 600; color: #1b4b31; background: #eaf4ee; border: 1px solid #a7f3d0; padding: 0.25rem 0.6rem; border-radius: 6px; display: inline-flex; align-items: center; gap: 0.35rem; }

        .ejc-right { display: flex; flex-direction: column; align-items: flex-end; gap: 0.4rem; flex-shrink: 0; }
        .ejc-status { display: inline-flex; align-items: center; gap: 0.35rem; font-size: 0.74rem; font-weight: 700; padding: 0.25rem 0.65rem; border-radius: 99px; background: #eaf4ee; color: #1b4b31; border: 1px solid #a7f3d0; }
        .ejc-status--Open { background: #eaf4ee; color: #1b4b31; border: 1px solid #a7f3d0; }
        .ejc-status--Paused { background: #f3f8f5; color: #486654; border: 1px solid #d8e6de; }
        .ejc-status--Closed { background: #f6faf7; color: #6f8c79; border: 1px solid #d8e6de; }

        .ejc-priority { font-size: 0.72rem; font-weight: 700; padding: 0.2rem 0.55rem; border-radius: 99px; display: inline-flex; align-items: center; gap: 0.3rem; background: #eaf4ee; color: #1b4b31; border: 1px solid #a7f3d0; }

        .ejc-stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 0.5rem; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 0.75rem; }
        .ejc-stat-tile { display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; border-right: 1px solid #e2e8f0; padding: 0 0.25rem; }
        .ejc-stat-tile:last-child { border-right: none; }
        .ejc-stat-val { font-size: 0.95rem; font-weight: 800; color: #0f172a; line-height: 1.1; }
        .ejc-stat-lbl { font-size: 0.68rem; font-weight: 700; color: #64748b; text-transform: uppercase; margin-top: 3px; letter-spacing: 0.03em; }
        .ejc-stat-tile--text .ejc-stat-val { font-size: 0.76rem; font-weight: 700; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 85px; }

        .ejc-actions { display: flex; gap: 0.45rem; flex-wrap: wrap; border-top: 1px solid #f1f5f9; padding-top: 0.85rem; }
        .ejc-action-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          padding: 0.45rem 0.85rem;
          font-size: 0.78rem;
          font-weight: 700;
          border-radius: 8px;
          border: 1px solid #d8e6de;
          background: #ffffff;
          color: #1b4b31;
          cursor: pointer;
          transition: all 0.18s ease;
          white-space: nowrap;
        }
        .ejc-action-btn:hover { background: #eaf4ee; border-color: #a7f3d0; color: #1b4b31; }
        .ejc-action-btn--delete { color: #1b4b31; background: #ffffff; border: 1px solid #d8e6de; }
        .ejc-action-btn--delete:hover { background: #eaf4ee; border-color: #a7f3d0; color: #1b4b31; }

        /* Modal */
        .modal-overlay {
          position: fixed;
          top: 0; left: 0; right: 0; bottom: 0;
          background-color: rgba(15, 23, 42, 0.65);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          padding: 1.25rem;
          box-sizing: border-box;
        }
        .modal-body {
          background: #ffffff;
          border-radius: 16px;
          width: 100%;
          max-width: 650px;
          max-height: 90vh;
          overflow-y: auto;
          padding: 1.75rem 2rem;
          box-shadow: 0 20px 25px -5px rgba(0,0,0,0.15);
        }
        .modal-header { display: flex; justify-content: space-between; align-items: center; padding-bottom: 1.1rem; border-bottom: 1px solid #f1f5f9; margin-bottom: 1.25rem; }
        .modal-title-group { display: flex; align-items: center; gap: 0.75rem; }
        .modal-title-icon { width: 40px; height: 40px; border-radius: 10px; background: #e6f4ed; color: #1b4b31; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .modal-title { margin: 0; font-size: 1.15rem; font-weight: 700; color: #0f172a; }
        .modal-subtitle { margin: 3px 0 0 0; font-size: 0.78rem; color: #64748b; }
        .modal-close-btn {
          display: flex; align-items: center; justify-content: center;
          width: 32px; height: 32px; border-radius: 50%;
          border: 1px solid #e2e8f0; background: #f8fafc; color: #64748b;
          cursor: pointer; transition: all 0.15s ease; padding: 0;
        }
        .modal-close-btn:hover { background: #fee2e2; color: #ef4444; border-color: #fca5a5; }
        .form-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 1.1rem; }
        @media (max-width: 600px) { .form-grid-2 { grid-template-columns: 1fr; } }
        .form-field { display: flex; flex-direction: column; gap: 0.4rem; }
        .form-label { font-size: 0.8rem; font-weight: 600; color: #334155; display: flex; align-items: center; gap: 0.25rem; }
        .form-label .required { color: #ef4444; }
        .modal-footer { display: flex; justify-content: flex-end; gap: 0.75rem; margin-top: 1.5rem; padding-top: 1rem; border-top: 1px solid #f1f5f9; }
        .btn-secondary { background: #fff; color: #334155; border: 1.5px solid #e2e8f0; border-radius: 9px; padding: 0.55rem 1.15rem; font-weight: 600; font-size: 0.86rem; cursor: pointer; }
        .btn-secondary:hover { background: #f8fafc; border-color: #cbd5e1; }
        .modal-body .ats-input, .modal-body .ats-select, .modal-body textarea.ats-input {
          width: 100%; box-sizing: border-box; padding: 0.6rem 0.85rem;
          border: 1.5px solid #cbd5e1; border-radius: 9px;
          font-size: 0.86rem; font-weight: 500; background: #ffffff; color: #0f172a;
          outline: none; transition: all 0.2s ease;
        }
        .modal-body .ats-input:focus, .modal-body .ats-select:focus, .modal-body textarea.ats-input:focus {
          border-color: #1b4b31; box-shadow: 0 0 0 3.5px rgba(27, 75, 49, 0.12);
        }
      `}</style>

      {/* Toolbar */}
      <div className="ats-toolbar">
        <div className="ats-search-box">
          <Search size={18} className="ats-search-icon" />
          <input
            type="text"
            className="ats-search-input"
            placeholder="Search job title, ID, department..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button
              type="button"
              className="ats-search-clear"
              onClick={() => setSearch("")}
              title="Clear search"
            >
              <X size={14} />
            </button>
          )}
        </div>

        <div className="ats-filter-group">
          <select className="ats-select" value={filterDept} onChange={(e) => setFilterDept(e.target.value)}>
            <option value="all">All Departments</option>
            {depts.map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
          <select className="ats-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">All Statuses</option>
            <option value="Open">Open</option>
            <option value="Paused">Paused</option>
            <option value="Closed">Closed</option>
          </select>
          <button className="btn-primary" onClick={handleOpenCreate}>
            <Plus size={16} /> Create Job Opening
          </button>
        </div>
      </div>

      {/* Job Cards */}
      {jobsLoading ? (
        <div style={{ textAlign: "center", padding: "3rem", color: "var(--text-muted)" }}>Loading Job Openings...</div>
      ) : filteredJobs.length === 0 ? (
        <div style={{ background: "var(--bg-card, #fff)", border: "1px dashed var(--border)", borderRadius: 12, padding: "3rem", textAlign: "center", color: "var(--text-muted)" }}>
          No job openings found matching criteria.
        </div>
      ) : (
        <div className="job-card-grid">
          {filteredJobs.map((job) => {
            const createdDate = job.createdAt?.toDate
              ? job.createdAt.toDate().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
              : job.createdAt
              ? new Date(job.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
              : "—";
            return (
              <div key={job.id} className="ejc-card">
                {/* Top section: left info + right badges */}
                <div className="ejc-top">
                  <div className="ejc-left">
                    <span className="ejc-id">JOB-{job.id.slice(0, 7).toUpperCase()}</span>
                    <div className="ejc-title">{job.title}</div>
                    <div className="ejc-meta">
                      <span className="ejc-meta-chip"><Building2 size={13} style={{ color: "#1b4b31" }} /> {job.department || "General"}</span>
                      <span className="ejc-meta-chip"><Briefcase size={13} style={{ color: "#1b4b31" }} /> {job.employmentType || "Full-Time"}</span>
                      <span className="ejc-meta-chip"><MapPin size={13} style={{ color: "#1b4b31" }} /> {job.location || "On-site"}</span>
                    </div>
                  </div>
                  <div className="ejc-right">
                    <span className={`ejc-status ejc-status--${job.status || "Open"}`}>
                      <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#1b4b31", display: "inline-block" }} />
                      {job.status || "Open"}
                    </span>
                    <span className={`ejc-priority ejc-priority--${job.priority || "Medium"}`}>
                      <AlertCircle size={13} style={{ color: "#1b4b31" }} /> {job.priority || "Medium"}
                    </span>
                  </div>
                </div>

                {/* Stats tiles */}
                <div className="ejc-stats">
                  <div className="ejc-stat-tile">
                    <span className="ejc-stat-val">{job.applicantCount ?? 0}</span>
                    <span className="ejc-stat-lbl">Applicants</span>
                  </div>
                  <div className="ejc-stat-tile">
                    <span className="ejc-stat-val">{job.vacancies ?? 1}</span>
                    <span className="ejc-stat-lbl">Vacancies</span>
                  </div>
                  <div className="ejc-stat-tile ejc-stat-tile--text">
                    <span className="ejc-stat-val">{job.hiringManager || "Unassigned"}</span>
                    <span className="ejc-stat-lbl">Hiring Mgr</span>
                  </div>
                  <div className="ejc-stat-tile ejc-stat-tile--text">
                    <span className="ejc-stat-val">{createdDate}</span>
                    <span className="ejc-stat-lbl">Created</span>
                  </div>
                </div>

                {/* Action bar */}
                <div className="ejc-actions">
                  <button className="ejc-action-btn ejc-action-btn--edit" onClick={() => handleOpenEdit(job)}>
                    <Edit2 size={13} /> Edit
                  </button>
                  <button className="ejc-action-btn ejc-action-btn--dup" onClick={() => handleDuplicate(job)}>
                    <Copy size={13} /> Duplicate
                  </button>
                  <button className="ejc-action-btn ejc-action-btn--view">
                    <Eye size={13} /> View
                  </button>
                  {job.status === "Open" && (
                    <button className="ejc-action-btn ejc-action-btn--close" onClick={() => handleCloseJob(job.id)}>
                      <Lock size={13} /> Close Job
                    </button>
                  )}
                  {isHR && (
                    <button
                      className="ejc-action-btn ejc-action-btn--delete"
                      onClick={() => handleInitiateDelete(job)}
                      title="Permanently Delete Job Opening"
                    >
                      <Trash2 size={13} /> Delete
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmJob && (
        <div className="modal-overlay" onClick={() => !deleting && setDeleteConfirmJob(null)}>
          <div
            className="modal-body"
            style={{ maxWidth: 480, padding: "1.75rem", borderRadius: 16, background: "#ffffff" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1rem" }}>
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: "50%",
                  background: "#fef2f2",
                  border: "1px solid #fecaca",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#dc2626",
                  flexShrink: 0,
                }}
              >
                <Trash2 size={22} />
              </div>
              <div>
                <h3 style={{ margin: 0, fontWeight: 800, fontSize: "1.1rem", color: "#0f172a" }}>
                  Delete Job Opening
                </h3>
                <span style={{ fontSize: "0.78rem", color: "#64748b" }}>
                  JOB-{deleteConfirmJob.id.slice(0, 7).toUpperCase()}
                </span>
              </div>
            </div>

            <p style={{ fontSize: "0.88rem", color: "#334155", lineHeight: 1.5, margin: "0 0 1.25rem" }}>
              Are you sure you want to permanently delete this job opening? This action cannot be undone.
            </p>

            <div
              style={{
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderRadius: 10,
                padding: "0.85rem 1rem",
                marginBottom: "1.25rem",
              }}
            >
              <div style={{ fontWeight: 700, fontSize: "0.9rem", color: "#0f172a" }}>
                {deleteConfirmJob.title}
              </div>
              <div style={{ fontSize: "0.78rem", color: "#64748b", marginTop: 2 }}>
                Department: {deleteConfirmJob.department || "General"} · Status: {deleteConfirmJob.status || "Open"}
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.75rem" }}>
              <button
                type="button"
                className="ejc-action-btn"
                style={{ padding: "0.55rem 1.15rem", borderRadius: 10, fontWeight: 600, fontSize: "0.86rem" }}
                disabled={deleting}
                onClick={() => setDeleteConfirmJob(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.4rem",
                  background: "#dc2626",
                  color: "#ffffff",
                  border: "none",
                  padding: "0.55rem 1.25rem",
                  borderRadius: 10,
                  fontWeight: 700,
                  fontSize: "0.86rem",
                  cursor: deleting ? "not-allowed" : "pointer",
                  boxShadow: "0 4px 12px rgba(220, 38, 38, 0.25)",
                }}
                disabled={deleting}
                onClick={handleConfirmDelete}
              >
                <Trash2 size={16} /> {deleting ? "Deleting..." : "Delete Job Opening"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Form */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-body" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title-group">
                <div className="modal-title-icon">
                  <Plus size={20} />
                </div>
                <div>
                  <h3 className="modal-title">
                    {editingJob ? "Edit Job Opening" : "Create New Job Opening"}
                  </h3>
                  <p className="modal-subtitle">Define position details, requirements, and hiring manager</p>
                </div>
              </div>
              <button
                type="button"
                className="modal-close-btn"
                onClick={() => setShowModal(false)}
                title="Close"
              >
                <X size={16} />
              </button>
            </div>

            <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
              <div className="form-grid-2">
                <div className="form-field">
                  <label className="form-label">
                    Job Title <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    className="ats-input"
                    required
                    placeholder="e.g. Senior Frontend Engineer"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  />
                </div>
                <div className="form-field">
                  <label className="form-label">
                    Department <span className="required">*</span>
                  </label>
                  <select
                    className="ats-select"
                    required
                    value={formData.department}
                    onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                  >
                    <option value="">Select Department</option>
                    {EMS_DEPARTMENTS.map((d) => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-grid-2">
                <div className="form-field">
                  <label className="form-label">Vacancies</label>
                  <input
                    type="number"
                    min="1"
                    className="ats-input"
                    value={formData.vacancies}
                    onChange={(e) => setFormData({ ...formData, vacancies: e.target.value })}
                  />
                </div>
                <div className="form-field">
                  <label className="form-label">Employment Type</label>
                  <select
                    className="ats-select"
                    value={formData.employmentType}
                    onChange={(e) => setFormData({ ...formData, employmentType: e.target.value })}
                  >
                    <option value="Full-Time">Full-Time</option>
                    <option value="Part-Time">Part-Time</option>
                    <option value="Contract">Contract</option>
                    <option value="Internship">Internship</option>
                  </select>
                </div>
              </div>

              <div className="form-grid-2">
                <div className="form-field">
                  <label className="form-label">Location</label>
                  <select
                    className="ats-select"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  >
                    <option value="On-site">On-site</option>
                    <option value="Remote">Remote</option>
                    <option value="Hybrid">Hybrid</option>
                  </select>
                </div>
                <div className="form-field">
                  <label className="form-label">Priority</label>
                  <select
                    className="ats-select"
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                  >
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                    <option value="Urgent">Urgent</option>
                  </select>
                </div>
              </div>

              <div className="form-grid-2">
                <div className="form-field">
                  <label className="form-label">Hiring Manager</label>
                  <input
                    type="text"
                    className="ats-input"
                    placeholder="e.g. Lead Name"
                    value={formData.hiringManager}
                    onChange={(e) => setFormData({ ...formData, hiringManager: e.target.value })}
                  />
                </div>
                <div className="form-field">
                  <label className="form-label">Deadline</label>
                  <input
                    type="date"
                    className="ats-input"
                    value={formData.deadline}
                    onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-field">
                <label className="form-label">Required Skills (comma separated)</label>
                <input
                  type="text"
                  className="ats-input"
                  placeholder="React, Node.js, TypeScript, SQL"
                  value={formData.skillsRequired}
                  onChange={(e) => setFormData({ ...formData, skillsRequired: e.target.value })}
                />
              </div>

              <div className="form-field">
                <label className="form-label">Job Description</label>
                <textarea
                  className="ats-input"
                  style={{ height: 80, resize: "vertical" }}
                  placeholder="Responsibilities, requirements, and job summary..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>

              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary">
                  {editingJob ? "Save Changes" : "Publish Job Opening"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
